import { createContext, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { AppState, Auth0Provider, useAuth0, User } from '@auth0/auth0-react';

import { LoadingSpinner, Result, Status } from '@oaknorth/component-core-v2';
import { T } from '@oaknorth/util-intl';
import { useNavigate } from '@oaknorth/util-routing';

import { UserInfo } from './types';
import { useAuth0Config } from './useAuth0Config';

export const UserContext = createContext<UserInfo | undefined>(undefined);
const BaseUserContextProvider = ({ children, logoutReturnTo }: PropsWithChildren<{ logoutReturnTo?: string }>) => {
  const { isLoading, error, isAuthenticated, loginWithRedirect, user, logout, getAccessTokenSilently } = useAuth0();

  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    const getAccessToken = async (): Promise<string | null> => {
      if (isAuthenticated) {
        try {
          const auth0AccessToken = await getAccessTokenSilently();
          setAccessToken(auth0AccessToken);
          return auth0AccessToken;
        } catch (error) {
          console.error(error);
          return null;
        }
      } else {
        return null;
      }
    };

    getAccessToken();
  }, [getAccessTokenSilently, isAuthenticated]);

  const userContextValue = useMemo(() => {
    if (!user || accessToken === null) {
      return undefined;
    }
    return {
      user: {
        userId: user.uid,
        tenantId: user.tenant_id,
        isONUser: user.onci_user,
        tenantName: user.tenant_name,
        firstName: user.given_name,
        lastName: user.family_name,
        email: user.email || '',
      },
      accessToken,
      onLogout: () =>
        logout({
          logoutParams: {
            returnTo: logoutReturnTo,
          },
        }),
    };
  }, [user, accessToken, logoutReturnTo, logout]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <Result status={Status.ERROR} title={error.name} text={error.message} />;
  }

  if (!isAuthenticated) {
    loginWithRedirect({ appState: { returnTo: window.location.pathname } });
    return <LoadingSpinner />;
  }

  return userContextValue ? (
    <UserContext.Provider value={userContextValue}>{children}</UserContext.Provider>
  ) : (
    <LoadingSpinner />
  );
};

// Exported for testing
export const AuthUserContextProvider = ({ children }: PropsWithChildren<{}>) => {
  const { isLoading, config } = useAuth0Config();
  const navigate = useNavigate();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (config === null) {
    return <Result status={Status.ERROR} title={T`user.error.title`} />;
  }

  const { domain, clientId, audience, scope, organization, connection, logoutReturnTo } = config;

  const redirectCallback = (appState: AppState | undefined, user: User | undefined) => {
    if (user?.custom_login_redirect_path) {
      navigate(user.custom_login_redirect_path);
    } else {
      appState?.returnTo && navigate(appState.returnTo);
    }
  };

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      onRedirectCallback={redirectCallback}
      authorizationParams={
        organization
          ? {
              redirect_uri: window.location.origin,
              audience,
              scope,
              organization,
            }
          : connection
          ? {
              redirect_uri: window.location.origin,
              audience,
              scope,
              connection,
            }
          : {
              redirect_uri: window.location.origin,
              audience,
              scope,
            }
      }
    >
      <BaseUserContextProvider logoutReturnTo={logoutReturnTo}>{children}</BaseUserContextProvider>
    </Auth0Provider>
  );
};
